import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTenant } from '../../context/TenantContext';

/** Resolve a public-folder asset URL respecting Vite's BASE_URL. */
const TOP_NAV_ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons/common-top-nav`;

/**
 * Routes on which the header should expose the **Organization** picker.
 *
 * Per UX: the Organization (a.k.a. "System") page is the only place a user
 * can switch tenants. Everywhere else the chosen org is locked-in and the
 * dropdown lets the user narrow the view to a specific **Site**.
 */
const ORG_PICKER_ROUTES = ['/system'];

/** Routes where the header tenant/site picker is intentionally hidden. */
const HIDE_PICKER_ROUTES = ['/checkout/checkouts', '/checkout/templates'];

function isOrgPickerRoute(pathname) {
  return ORG_PICKER_ROUTES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

function isHidePickerRoute(pathname) {
  return HIDE_PICKER_ROUTES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}

function getDisplayName(user) {
  const explicitName = user?.name?.trim();
  if (explicitName) return explicitName;

  const email = user?.email?.trim();
  if (!email) return 'Signed-in user';

  return email.split('@')[0];
}

function getInitials(user) {
  const name = getDisplayName(user);
  const parts = name.split(/\s+/).filter(Boolean);

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  return name.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2).toUpperCase() || 'DU';
}

function Header() {
  const { pathname } = useLocation();
  const orgMode = isOrgPickerRoute(pathname);
  const hidePicker = isHidePickerRoute(pathname);
  const { user, roles, logout } = useAuth();

  const {
    organizations,
    currentOrg,
    orgId,
    setOrgId,
    sites,
    currentSite,
    siteId,
    setSiteId,
  } = useTenant();

  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const pickerRef = useRef(null);
  const userMenuRef = useRef(null);

  const displayName = useMemo(() => getDisplayName(user), [user]);
  const initials = useMemo(() => getInitials(user), [user]);
  const emailLabel = user?.email || 'No email available';
  const primaryRole = roles?.[0]?.roleName || 'No role assigned';
  const normalizedPickerSearch = pickerSearch.trim().toLowerCase();
  const filteredOrganizations = useMemo(() => organizations.filter((organization) => {
    if (!normalizedPickerSearch) return true;
    const id = organization.orgId ?? organization.id;
    const label = organization.displayName || organization.name || `Org ${id}`;
    return `${label} ${id}`.toLowerCase().includes(normalizedPickerSearch);
  }), [organizations, normalizedPickerSearch]);
  const filteredSites = useMemo(() => sites.filter((site) => {
    if (!normalizedPickerSearch) return true;
    const id = site.siteId ?? site.id;
    const label = site.siteName || site.name || `Site ${id}`;
    return `${label} ${id}`.toLowerCase().includes(normalizedPickerSearch);
  }), [sites, normalizedPickerSearch]);

  // Close open menus on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setOpen(false);
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Close the picker whenever the user navigates to a different route so
  // we don't leave a stale "Org" menu open on a "Site" route (or vice versa).
  useEffect(() => {
    setOpen(false);
    setUserMenuOpen(false);
  }, [pathname]);

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  const togglePicker = () => {
    if (!open) setPickerSearch('');
    setOpen((value) => !value);
  };

  const orgLabel = currentOrg?.displayName || currentOrg?.name || 'Select organization';
  const siteLabel = currentSite
    ? (currentSite.siteName || currentSite.name || `Site ${currentSite.siteId ?? currentSite.id}`)
    : 'All sites';
  const triggerLabel = orgMode ? orgLabel : siteLabel;
  const triggerTitle = orgMode
    ? `Organization: ${orgLabel}`
    : `${orgLabel} — Site: ${siteLabel}`;

  return (
    <div className="top-header">
      <div></div>
      <div className="header-right">
        {!hidePicker && (
        <div className="site-picker" ref={pickerRef}>
          <button
            type="button"
            className="site-picker-trigger"
            onClick={togglePicker}
            title={triggerTitle}
          >
            {triggerLabel}
            <i className="fas fa-chevron-down"></i>
          </button>
          {open && (
            <div className="site-picker-menu">
              {orgMode ? (
                /* ── Organization picker (only on the Organization / System page) ── */
                <div className="site-picker-group">
                  <div className="site-picker-label">Organization</div>
                  <input
                    type="search"
                    className="site-picker-search"
                    value={pickerSearch}
                    onChange={(event) => setPickerSearch(event.target.value)}
                    placeholder="Search organizations"
                    aria-label="Search organizations"
                    autoFocus
                  />
                  <ul className="site-picker-list">
                    {filteredOrganizations.map((o) => {
                      const id = o.orgId ?? o.id;
                      const label = o.displayName || o.name || `Org ${id}`;
                      const isSelected = id === orgId;
                      return (
                        <li
                          key={id}
                          className={`site-picker-item${isSelected ? ' is-active' : ''}`}
                          onClick={() => { setOrgId(id); setOpen(false); }}
                        >
                          {label}
                        </li>
                      );
                    })}
                    {filteredOrganizations.length === 0 && (
                      <li className="site-picker-hint">No matching organizations.</li>
                    )}
                  </ul>
                </div>
              ) : (
                /* ── Site picker (every other page) ─────────────────────────── */
                <div className="site-picker-group">
                  <div className="site-picker-label">
                    Site
                    {currentOrg && (
                      <span className="site-picker-sublabel">
                        {' '}· {currentOrg.displayName || currentOrg.name}
                      </span>
                    )}
                  </div>
                  <input
                    type="search"
                    className="site-picker-search"
                    value={pickerSearch}
                    onChange={(event) => setPickerSearch(event.target.value)}
                    placeholder="Search sites"
                    aria-label="Search sites"
                    autoFocus
                  />
                  {!orgId ? (
                    <div className="site-picker-hint">
                      Select an organization on the System page first.
                    </div>
                  ) : (
                    <ul className="site-picker-list">
                      <li
                        className={`site-picker-item${!siteId ? ' is-active' : ''}`}
                        onClick={() => { setSiteId(null); setOpen(false); }}
                      >
                        All sites
                      </li>
                      {filteredSites.map((s) => {
                        const id = s.siteId ?? s.id;
                        const label = s.siteName || s.name || `Site ${id}`;
                        const isSelected = id === siteId;
                        return (
                          <li
                            key={id}
                            className={`site-picker-item${isSelected ? ' is-active' : ''}`}
                            onClick={() => { setSiteId(id); setOpen(false); }}
                          >
                            {label}
                          </li>
                          );
                        })}
                      {filteredSites.length === 0 && (
                        <li className="site-picker-hint">No matching sites.</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        )}
        <button className="header-icon-btn" title="Help">
          <img src={`${TOP_NAV_ICON_BASE}/questionmark.svg`} alt="" className="header-icon-img" />
        </button>
        <button className="header-icon-btn" title="Notifications">
          <img src={`${TOP_NAV_ICON_BASE}/notification.svg`} alt="" className="header-icon-img" />
        </button>
        <button className="header-icon-btn" title="Apps">
          <img src={`${TOP_NAV_ICON_BASE}/9dots.svg`} alt="" className="header-icon-img" />
        </button>
        <div className="header-user" ref={userMenuRef}>
          <button
            type="button"
            className="header-user-trigger"
            onClick={() => setUserMenuOpen((value) => !value)}
            title={`${displayName} (${emailLabel})`}
          >
            <div className="header-user-text">
              <div className="header-user-name">{displayName}</div>
              <div className="header-user-meta">{emailLabel}</div>
            </div>
            <div className="header-user-avatar">{initials}</div>
          </button>

          {userMenuOpen && (
            <div className="header-user-menu">
              <div className="header-user-menu-label">Signed in as</div>
              <div className="header-user-menu-name">{displayName}</div>
              <div className="header-user-menu-email">{emailLabel}</div>
              <div className="header-user-menu-role">{primaryRole}</div>
              <button type="button" className="header-user-logout" onClick={handleLogout}>
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Header;
